import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import usuarioService, { Usuario } from '../../services/usuarioService';
import permisoUsuarioService from '../../services/permisoUsuarioService';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import CajasUsuarioTab from '../../components/sistema/CajasUsuarioTab';
import PermisosUsuarioTab from '../../components/sistema/PermisosUsuarioTab';
import UbicacionesUsuarioSection from '../../components/sistema/UbicacionesUsuarioSection';
import UbicacionPredeterminadaAdminSection from '../../components/sistema/UbicacionPredeterminadaAdminSection';
import SucursalesUsuarioSection from '../../components/sistema/SucursalesUsuarioSection';
import SucursalPredeterminadaAdminSection from '../../components/sistema/SucursalPredeterminadaAdminSection';
import SegundoFactorTab from '../../components/sistema/SegundoFactorTab';
import SearchableSelect from '../../components/SearchableSelect';
import ubicacionXUsuarioService from '../../services/ubicacionXUsuarioService';
import sucursalXUsuarioService from '../../services/sucursalXUsuarioService';
import {
  mapAsignacionesToDraft,
  syncUbicacionesUsuario,
  syncUbicacionPredeterminadaAdmin,
  extractUbicacionPredeterminadaAdmin,
  type UbicacionUsuarioDraft,
} from '../../utils/syncUbicacionesUsuario';
import {
  mapAsignacionesToDraft as mapSucursalesAsignacionesToDraft,
  syncSucursalesUsuario,
  syncSucursalPredeterminadaAdmin,
  extractSucursalPredeterminadaAdmin,
  type SucursalUsuarioDraft,
} from '../../utils/syncSucursalesUsuario';
import { isAdminRoleName } from '../../utils/ubicacionUsuarioRoles';
import {
  canManageSistemaUsers,
  effectiveUserRoles,
  effectiveUsername,
} from '../../utils/sistemaAuthz';
import { bypassesBusinessPermissions } from '../../utils/permisoAuthz';

interface UsuarioFormData {
  usuario: string;
  nombre: string;
  password: string;
  email: string;
  role: string;
}

const defaultForm: UsuarioFormData = {
  usuario: '',
  nombre: '',
  password: '',
  email: '',
  role: 'User',
};

/** Misma lógica de referencia que en API (EmailAddressAttribute es algo más permisiva; esto cubre casos habituales). */
const LOGIN_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const baseRoles = ['Admin', 'User', 'Vendedor', 'Supervisor', 'Operador'];

const UsuarioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { reload: reloadPermisosSesion } = usePermissions();
  const { usuario: usuarioParam } = useParams();
  const [formData, setFormData] = useState<UsuarioFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [frozenRole, setFrozenRole] = useState<string>('User');
  const [ubicacionesDraft, setUbicacionesDraft] = useState<UbicacionUsuarioDraft[]>([]);
  const [ubicacionesOriginales, setUbicacionesOriginales] = useState<UbicacionUsuarioDraft[]>([]);
  const [adminUbicacionPredeterminada, setAdminUbicacionPredeterminada] = useState<UbicacionUsuarioDraft | null>(null);
  const [sucursalesDraft, setSucursalesDraft] = useState<SucursalUsuarioDraft[]>([]);
  const [sucursalesOriginales, setSucursalesOriginales] = useState<SucursalUsuarioDraft[]>([]);
  const [adminSucursalPredeterminada, setAdminSucursalPredeterminada] = useState<SucursalUsuarioDraft | null>(null);
  const [permisosGranted, setPermisosGranted] = useState<string[]>([]);

  const canManageUsers = useMemo(
    () => canManageSistemaUsers(effectiveUserRoles(user?.roles)),
    [user?.roles]
  );
  const selfLogin = useMemo(() => effectiveUsername(user?.username), [user?.username]);
  const isSelfProfileRoute = useMemo(
    () =>
      !!(
        selfLogin &&
        usuarioParam &&
        usuarioParam !== 'nuevo' &&
        selfLogin.toLowerCase() === usuarioParam.toLowerCase()
      ),
    [selfLogin, usuarioParam]
  );
  const isLimitedSelfProfile = !canManageUsers && isSelfProfileRoute;

  // Solo mostrar SUPERADMIN si el usuario actual es SUPERADMIN
  const isSuperAdmin = user?.roles?.includes('SUPERADMIN');
  const availableRoles = isSuperAdmin ? [...baseRoles, 'SUPERADMIN'] : baseRoles;

  const afterSaveOrCancelPath = canManageUsers ? '/sistema/usuarios' : '/menu-principal';

  const loadUsuario = useCallback(async () => {
    if (!usuarioParam || usuarioParam === 'nuevo') return;
    const manage = canManageSistemaUsers(effectiveUserRoles(user?.roles));
    const self = effectiveUsername(user?.username);
    if (!manage) {
      if (!self || usuarioParam.toLowerCase() !== self.toLowerCase()) return;
    }
    try {
      setLoading(true);
      const usuario = await usuarioService.getUsuario(usuarioParam);
      const role = usuario.role || 'User';
      setFrozenRole(role);
      setFormData({
        usuario: (usuario as any).usuario || (usuario as any).Usuario || (usuario as any).id || (usuario as any).Id || '',
        nombre: (usuario as any).username || (usuario as any).Username || (usuario as any).nombre || (usuario as any).Nombre || '',
        password: '',
        email: usuario.email || '',
        role,
      });
      setIsEdit(true);

      if (canManageSistemaUsers(effectiveUserRoles(user?.roles))) {
        try {
          const asignadas = await ubicacionXUsuarioService.getByUsuario(usuarioParam);
          if (isAdminRoleName(role)) {
            const predeterminada = extractUbicacionPredeterminadaAdmin(asignadas);
            const originales = mapAsignacionesToDraft(asignadas);
            setAdminUbicacionPredeterminada(predeterminada);
            setUbicacionesOriginales(originales);
            setUbicacionesDraft([]);
          } else {
            const draft = mapAsignacionesToDraft(asignadas);
            setUbicacionesDraft(draft);
            setUbicacionesOriginales(draft);
            setAdminUbicacionPredeterminada(null);
          }
        } catch {
          setUbicacionesDraft([]);
          setUbicacionesOriginales([]);
          setAdminUbicacionPredeterminada(null);
        }

        try {
          const sucAsignadas = await sucursalXUsuarioService.getByUsuario(usuarioParam);
          if (isAdminRoleName(role)) {
            const predeterminada = extractSucursalPredeterminadaAdmin(sucAsignadas);
            const originales = mapSucursalesAsignacionesToDraft(sucAsignadas);
            setAdminSucursalPredeterminada(predeterminada);
            setSucursalesOriginales(originales);
            setSucursalesDraft([]);
          } else {
            const draft = mapSucursalesAsignacionesToDraft(sucAsignadas);
            setSucursalesDraft(draft);
            setSucursalesOriginales(draft);
            setAdminSucursalPredeterminada(null);
          }
        } catch {
          setSucursalesDraft([]);
          setSucursalesOriginales([]);
          setAdminSucursalPredeterminada(null);
        }

        if (bypassesBusinessPermissions([role])) {
          setPermisosGranted([]);
        } else {
          try {
            const matriz = await permisoUsuarioService.getMatriz(usuarioParam);
            setPermisosGranted(matriz.granted);
          } catch {
            setPermisosGranted([]);
          }
        }
      } else {
        setUbicacionesDraft([]);
        setUbicacionesOriginales([]);
        setAdminUbicacionPredeterminada(null);
        setSucursalesDraft([]);
        setSucursalesOriginales([]);
        setAdminSucursalPredeterminada(null);
        setPermisosGranted([]);
      }
    } catch {
      enqueueSnackbar('Error al cargar el usuario', { variant: 'error' });
      navigate(canManageSistemaUsers(effectiveUserRoles(user?.roles)) ? '/sistema/usuarios' : '/menu-principal');
    } finally {
      setLoading(false);
    }
  }, [usuarioParam, enqueueSnackbar, navigate, user?.roles, user?.username]);

  useEffect(() => {
    if (usuarioParam && usuarioParam !== 'nuevo') {
      loadUsuario();
    } else if (usuarioParam === 'nuevo') {
      setUbicacionesDraft([]);
      setUbicacionesOriginales([]);
      setAdminUbicacionPredeterminada(null);
      setSucursalesDraft([]);
      setSucursalesOriginales([]);
      setAdminSucursalPredeterminada(null);
      setPermisosGranted([]);
    }
  }, [usuarioParam, loadUsuario]);

  const showUbicacionesTab = !isLimitedSelfProfile;
  const showSecondFactorTab = isEdit && isSelfProfileRoute;
  const showUserTabs = !isLimitedSelfProfile || showSecondFactorTab;
  const showUbicacionesSection =
    showUbicacionesTab && currentTab === 1 && !isAdminRoleName(formData.role);
  const showAdminUbicacionPredeterminada =
    showUbicacionesTab && currentTab === 1 && isAdminRoleName(formData.role);
  const showSucursalesSection =
    showUbicacionesTab && currentTab === 2 && !isAdminRoleName(formData.role);
  const showAdminSucursalPredeterminada =
    showUbicacionesTab && currentTab === 2 && isAdminRoleName(formData.role);

  useEffect(() => {
    if (isLimitedSelfProfile) setCurrentTab(0);
  }, [isLimitedSelfProfile]);

  useEffect(() => {
    if (!showSecondFactorTab && currentTab === 5) setCurrentTab(0);
  }, [showSecondFactorTab, currentTab]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);

      if (!formData.usuario.trim()) {
        enqueueSnackbar('El ID de usuario es requerido', { variant: 'warning' });
        return;
      }

      if (!formData.nombre.trim()) {
        enqueueSnackbar('El nombre es requerido', { variant: 'warning' });
        return;
      }

      const usuarioTrim = formData.usuario.trim();
      if (usuarioTrim.length > 30) {
        enqueueSnackbar('El ID de usuario no puede superar los 30 caracteres', { variant: 'warning' });
        return;
      }

      const emailTrim = formData.email.trim();
      if (emailTrim && !LOGIN_EMAIL_REGEX.test(emailTrim)) {
        enqueueSnackbar('El email opcional no tiene un formato válido', { variant: 'warning' });
        return;
      }

      if (!isLimitedSelfProfile) {
        if (formData.role.toUpperCase() === 'SUPERADMIN' && !isSuperAdmin) {
          enqueueSnackbar('Solo usuarios con rol SUPERADMIN pueden asignar este rol', { variant: 'error' });
          return;
        }
      }

      if (!isEdit && (!formData.password || formData.password.length < 6)) {
        enqueueSnackbar('La contraseña debe tener al menos 6 caracteres', { variant: 'warning' });
        return;
      }

      const roleForApi = isLimitedSelfProfile ? frozenRole : formData.role;

      const payload: Usuario = {
        id: usuarioTrim,
        usuario: usuarioTrim,
        username: formData.nombre.trim(),
        nombre: formData.nombre.trim(),
        ...(emailTrim ? { email: emailTrim } : {}),
        role: roleForApi,
        ...(formData.password && { password: formData.password }),
      };

      if (isEdit) {
        await usuarioService.updateUsuario(usuarioParam!, payload);
      } else {
        await usuarioService.createUsuario(payload);
      }

      if (!isLimitedSelfProfile && isAdminRoleName(roleForApi)) {
        try {
          await syncUbicacionPredeterminadaAdmin(
            usuarioTrim,
            ubicacionesOriginales,
            adminUbicacionPredeterminada
          );
        } catch (syncError: any) {
          const syncMsg = syncError?.response?.data?.message || 'Error al guardar ubicación por defecto';
          enqueueSnackbar(
            isEdit ? `Usuario guardado, pero ${syncMsg}` : `Usuario creado, pero ${syncMsg}`,
            { variant: 'warning' }
          );
          navigate(afterSaveOrCancelPath);
          return;
        }

        try {
          await syncSucursalPredeterminadaAdmin(
            usuarioTrim,
            sucursalesOriginales,
            adminSucursalPredeterminada
          );
        } catch (syncError: any) {
          const syncMsg = syncError?.response?.data?.message || 'Error al guardar sucursal por defecto';
          enqueueSnackbar(
            isEdit ? `Usuario guardado, pero ${syncMsg}` : `Usuario creado, pero ${syncMsg}`,
            { variant: 'warning' }
          );
          navigate(afterSaveOrCancelPath);
          return;
        }
      } else if (!isLimitedSelfProfile && !isAdminRoleName(roleForApi)) {
        const dup = ubicacionesDraft.some(
          (u, idx, arr) => arr.findIndex((x) => x.nroUbicacion === u.nroUbicacion) !== idx
        );
        if (dup) {
          enqueueSnackbar('Hay ubicaciones duplicadas en la asignación', { variant: 'warning' });
          return;
        }
        if (ubicacionesDraft.length > 0 && !ubicacionesDraft.some((u) => u.preferida)) {
          enqueueSnackbar('Debe marcar una ubicación como preferida', { variant: 'warning' });
          return;
        }

        const dupSuc = sucursalesDraft.some(
          (s, idx, arr) => arr.findIndex((x) => x.nroSuc === s.nroSuc) !== idx
        );
        if (dupSuc) {
          enqueueSnackbar('Hay sucursales duplicadas en la asignación', { variant: 'warning' });
          return;
        }
        if (sucursalesDraft.length > 0 && !sucursalesDraft.some((s) => s.preferida)) {
          enqueueSnackbar('Debe marcar una sucursal como preferida', { variant: 'warning' });
          return;
        }

        try {
          await syncUbicacionesUsuario(
            usuarioTrim,
            isEdit ? ubicacionesOriginales : [],
            ubicacionesDraft
          );
        } catch (syncError: any) {
          const syncMsg = syncError?.response?.data?.message || 'Error al guardar ubicaciones del usuario';
          enqueueSnackbar(
            isEdit
              ? `Usuario guardado, pero ${syncMsg}`
              : `Usuario creado, pero ${syncMsg}`,
            { variant: 'warning' }
          );
          navigate(afterSaveOrCancelPath);
          return;
        }

        try {
          await syncSucursalesUsuario(
            usuarioTrim,
            isEdit ? sucursalesOriginales : [],
            sucursalesDraft
          );
        } catch (syncError: any) {
          const syncMsg = syncError?.response?.data?.message || 'Error al guardar sucursales del usuario';
          enqueueSnackbar(
            isEdit
              ? `Usuario guardado, pero ${syncMsg}`
              : `Usuario creado, pero ${syncMsg}`,
            { variant: 'warning' }
          );
          navigate(afterSaveOrCancelPath);
          return;
        }
      }

      if (!isLimitedSelfProfile && !bypassesBusinessPermissions([roleForApi])) {
        try {
          await permisoUsuarioService.saveMatriz(usuarioTrim, permisosGranted);
          if (selfLogin && usuarioTrim.toLowerCase() === selfLogin.toLowerCase()) {
            await reloadPermisosSesion();
          }
        } catch (permError: any) {
          const permMsg = permError?.response?.data?.message || 'Error al guardar permisos del usuario';
          enqueueSnackbar(
            isEdit ? `Usuario guardado, pero ${permMsg}` : `Usuario creado, pero ${permMsg}`,
            { variant: 'warning' }
          );
          navigate(afterSaveOrCancelPath);
          return;
        }
      }

      enqueueSnackbar('Usuario guardado exitosamente', { variant: 'success' });
      navigate(afterSaveOrCancelPath);
    } catch (error: any) {
      console.error('Error al guardar usuario:', error);
      const errorMessage = error?.response?.data?.message || 'Error al guardar el usuario';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const blockingOtherProfile =
    !canManageUsers &&
    !!usuarioParam &&
    usuarioParam !== 'nuevo' &&
    (!selfLogin || usuarioParam.toLowerCase() !== selfLogin.toLowerCase());

  if (blockingOtherProfile) {
    return (
      <Navigate
        to={selfLogin ? `/sistema/usuarios/${encodeURIComponent(selfLogin)}` : '/menu-principal'}
        replace
      />
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          bgcolor: '#f5f5f5',
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const pageTitle = isLimitedSelfProfile
    ? 'Mis datos'
    : isEdit
      ? 'Editar usuario del tenant'
      : 'Nuevo usuario del tenant';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: !isLimitedSelfProfile ? 1 : 3, flexWrap: 'wrap', gap: 1 }}
          >
            {!isLimitedSelfProfile && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(afterSaveOrCancelPath)}
                sx={{ textTransform: 'none' }}
              >
                Usuarios del tenant
              </Button>
            )}
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {pageTitle}
            </Typography>
          </Stack>
          {!isLimitedSelfProfile && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Los cambios aplican solo al tenant en el que estás trabajando.
            </Typography>
          )}

          {showUserTabs && (
            <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
              <Tab label="General" />
              {!isLimitedSelfProfile && <Tab label="Ubicaciones" />}
              {!isLimitedSelfProfile && <Tab label="Sucursales de venta" />}
              {!isLimitedSelfProfile && <Tab label="Grupos de Caja" disabled={!isEdit} />}
              {!isLimitedSelfProfile && <Tab label="Permisos" />}
              {showSecondFactorTab && <Tab value={5} label="Segundo Factor" />}
            </Tabs>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="usuario"
                    label={isEdit ? 'Usuario' : 'ID de usuario'}
                    type="text"
                    value={formData.usuario}
                    onChange={handleChange}
                    fullWidth
                    required
                    disabled={isEdit || isLimitedSelfProfile}
                    inputProps={
                      isEdit || isLimitedSelfProfile
                        ? { autoComplete: 'off' }
                        : { maxLength: 30, autoComplete: 'username' }
                    }
                    helperText={
                      isLimitedSelfProfile || isEdit
                        ? 'ID de login'
                        : 'Identificador único para iniciar sesión (máx. 30 caracteres)'
                    }
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="nombre"
                    label="Nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    fullWidth
                    required
                    helperText="Nombre completo del usuario"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="email"
                    label="Email (opcional)"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    helperText="Mail de contacto y recuperación de contraseña"
                    inputProps={{ autoComplete: 'email' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {isLimitedSelfProfile ? (
                    <TextField
                      label="Rol"
                      value={formData.role}
                      fullWidth
                      disabled
                      helperText="Solo un administrador puede cambiar el rol"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  ) : (
                    <SearchableSelect
                      name="role"
                      label="Rol"
                      required
                      value={formData.role || null}
                      onChange={(v) => setFormData((prev) => ({ ...prev, role: v ?? '' }))}
                      options={availableRoles.map((role) => ({ value: role, label: role }))}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="password"
                    label={isEdit ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    fullWidth
                    required={!isEdit}
                    helperText={isEdit ? 'Dejar vacío si no desea cambiar la contraseña' : 'Mínimo 6 caracteres'}
                    inputProps={{ autoComplete: 'new-password' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {showUbicacionesSection && (
              <UbicacionesUsuarioSection
                value={ubicacionesDraft}
                onChange={setUbicacionesDraft}
              />
            )}

            {showAdminUbicacionPredeterminada && (
              <UbicacionPredeterminadaAdminSection
                value={adminUbicacionPredeterminada}
                onChange={setAdminUbicacionPredeterminada}
              />
            )}

            {showSucursalesSection && (
              <SucursalesUsuarioSection
                value={sucursalesDraft}
                onChange={setSucursalesDraft}
              />
            )}

            {showAdminSucursalPredeterminada && (
              <SucursalPredeterminadaAdminSection
                value={adminSucursalPredeterminada}
                onChange={setAdminSucursalPredeterminada}
              />
            )}

            {!isLimitedSelfProfile && currentTab === 3 && (
              <CajasUsuarioTab usuario={formData.usuario} username={formData.nombre} disabled={!isEdit} />
            )}

            {!isLimitedSelfProfile && currentTab === 4 && (
              <PermisosUsuarioTab
                username={formData.usuario || usuarioParam || ''}
                currentUsername={selfLogin}
                role={formData.role}
                granted={permisosGranted}
                onChange={setPermisosGranted}
                disabled={saving}
                locked={bypassesBusinessPermissions([formData.role])}
              />
            )}

            {showSecondFactorTab && currentTab === 5 && <SegundoFactorTab />}

            {currentTab !== 5 && (
              <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={saving}
                  sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                >
                  {saving ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(afterSaveOrCancelPath)}
                  disabled={saving}
                  sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                >
                  Cancelar
                </Button>
              </Stack>
            )}
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UsuarioForm;
